import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';

const DarkModeContext = createContext();

export const useDarkMode = () => useContext(DarkModeContext);

const STORAGE_KEY = 'agorax_dark_mode';

const LIGHT = {
  pri: '#0B1A4A',
  pri2: '#2952CC',
  priLight: '#eff6ff',
  green: '#34a853',
  red: '#ea4335',
  amber: '#f59e0b',
  muted: '#6B7280',
  faint: '#9aa0a6',
  bg: '#F5F7FA',
  bg2: '#EDF0F5',
  white: '#ffffff',
  border: '#E8EAEF',
  text: '#1F2937',
  surface: '#ffffff',
  surfaceAlt: '#F5F7FA',
  inputBg: '#f8fafc',
  greenBg: '#e6f4ea',
  redBg: '#fce8e6',
  amberBg: '#fef7e0',
  blueBg: '#e8f0fe',
  successBg: '#ecfdf5',
  dangerBg: '#fef2f2',
  warningBg: '#fffbeb',
  infoBg: '#eff6ff',
  successText: '#065f46',
  dangerText: '#991b1b',
  warningText: '#92400e',
  infoText: '#1e40af',
  overlay: 'rgba(0,0,0,0.4)',
  skeleton: '#e2e8f0',
  orange: '#ff6d00',
  canvasBg: '#e8eaee',
  sidebarBg: '#0B1A4A',
  headerBg: '#FFFFFF',
  headerBorder: '#F0F1F5',
  headerText: '#0B1A4A',
  contentBg: '#F5F7FA',
  linkColor: '#2952CC',
};

const DARK = {
  pri: '#e2e8f0',
  pri2: '#60a5fa',
  priLight: '#1e293b',
  green: '#34d399',
  red: '#f87171',
  amber: '#fbbf24',
  muted: '#64748b',
  faint: '#475569',
  bg: '#0f172a',
  bg2: '#1e293b',
  white: '#1e293b',
  border: '#334155',
  text: '#e2e8f0',
  surface: '#1e293b',
  surfaceAlt: '#0f172a',
  inputBg: '#0f172a',
  greenBg: '#064e3b',
  redBg: '#7f1d1d',
  amberBg: '#78350f',
  blueBg: '#1e3a5f',
  successBg: '#064e3b',
  dangerBg: '#7f1d1d',
  warningBg: '#78350f',
  infoBg: '#1e3a5f',
  successText: '#6ee7b7',
  dangerText: '#fca5a5',
  warningText: '#fcd34d',
  infoText: '#93c5fd',
  overlay: 'rgba(0,0,0,0.75)',
  skeleton: '#334155',
  orange: '#fb923c',
  canvasBg: '#1e293b',
  sidebarBg: '#0f1119',
  headerBg: '#1e293b',
  headerBorder: '#334155',
  headerText: '#e2e8f0',
  contentBg: '#0f172a',
  linkColor: '#93c5fd',
};

const VAR_MAP = {
  pri: 'pri',
  pri2: 'pri2',
  priLight: 'pri-light',
  green: 'green',
  red: 'red',
  amber: 'amber',
  muted: 'muted',
  faint: 'faint',
  bg: 'bg',
  bg2: 'bg2',
  white: 'surface',
  border: 'border',
  text: 'text',
  surface: 'surface-card',
  surfaceAlt: 'surface-alt',
  inputBg: 'input-bg',
  greenBg: 'green-bg',
  redBg: 'red-bg',
  amberBg: 'amber-bg',
  blueBg: 'blue-bg',
  successBg: 'success-bg',
  dangerBg: 'danger-bg',
  warningBg: 'warning-bg',
  infoBg: 'info-bg',
  successText: 'success-text',
  dangerText: 'danger-text',
  warningText: 'warning-text',
  infoText: 'info-text',
  overlay: 'overlay',
  skeleton: 'skeleton',
  orange: 'orange',
  canvasBg: 'canvas-bg',
  sidebarBg: 'sidebar-bg',
  headerBg: 'header-bg',
  headerBorder: 'header-border',
  headerText: 'header-text',
  contentBg: 'content-bg',
  linkColor: 'link',
};

function buildCSSVars(colors) {
  return Object.entries(colors)
    .map(([key, val]) => `--agx-${VAR_MAP[key] || key}: ${val};`)
    .join('\n');
}

const LIGHT_VARS = buildCSSVars(LIGHT);
const DARK_VARS = buildCSSVars(DARK);

export const DarkModeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved !== null) return saved === 'true';
    } catch {}
    return false;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(darkMode));
    } catch {}
    const colors = darkMode ? DARK : LIGHT;
    document.documentElement.setAttribute('data-dark', darkMode ? 'true' : 'false');

    let styleEl = document.getElementById('agorax-theme-vars');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'agorax-theme-vars';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `:root {\n${darkMode ? DARK_VARS : LIGHT_VARS}\n}`;

    document.body.style.backgroundColor = colors.bg;
    document.body.style.color = colors.text;
  }, [darkMode]);

  useEffect(() => {
    const lightVars = LIGHT_VARS;
    const styleEl = document.createElement('style');
    styleEl.id = 'agorax-theme-vars';
    styleEl.textContent = `:root {\n${lightVars}\n}`;
    const existing = document.getElementById('agorax-theme-vars');
    if (!existing) document.head.appendChild(styleEl);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => !prev);
  }, []);

  const COL = useMemo(() => (darkMode ? DARK : LIGHT), [darkMode]);

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode, COL }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeContext;
